import React, { Fragment, useContext, useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import profile_img from '../../assets/images/pages/defaultImage.jpg';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import Spinner from '../../components/common/spinner';
import Swal from 'sweetalert2';
import BreadCrumb from '../../components/common/BreadCrumb';
import { numberWithCommas } from '../../utils/validations';
import henceofrthEnums from '../../utils/henceofrthEnums';
import { toast } from 'react-toastify';
import VerifyPickupOtpModal from '../../components/modals/VerifyPickupOtp';
import { getStatusBadge } from '../../utils/orderStatusBadge';
import { Button } from '../../components/ui/button';
import moment from 'moment';
import {
  Package,
  Receipt,
  User,
  Truck,
  Star,
  FileText,
  AlertCircle,
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
} from 'lucide-react';

/**
 * Product Item Renderer with thumbnail selector and key specs (clean, borderless layout)
 */
const ProductItem: React.FC<{
  product: any;
  orderD: any;
  language_data: any;
  authState: any;
}> = ({ product, orderD, language_data, authState }) => {
  const [selectedImgIndex, setSelectedImgIndex] = useState(0);
  const [showMore, setShowMore] = useState(false);

  const images: string[] = useMemo(() => {
    if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }
    return [];
  }, [product?.images]);

  const activeImg = images[selectedImgIndex]
    ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${images[selectedImgIndex]}`
    : profile_img;

  const description = product?.description || '';
  const isLongDescription = description.length > 300;

  return (
    <div className="py-2">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Product Image & Thumbnails */}
        <div className="w-full md:w-52 shrink-0 space-y-2.5">
          <div className="relative aspect-square w-full rounded-2xl overflow-hidden bg-slate-50/80 flex items-center justify-center p-3 group">
            <img
              src={activeImg}
              alt={product?.name || 'Product'}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                (e.target as HTMLImageElement).src = profile_img;
              }}
            />
            {product?.discount_percantage > 0 && (
              <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-500 text-white shadow-2xs">
                {product.discount_percantage}% OFF
              </span>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedImgIndex(idx)}
                  className={`relative w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-slate-50 transition-all cursor-pointer ${selectedImgIndex === idx
                    ? 'ring-2 ring-[#1AA7DE] opacity-100'
                    : 'opacity-60 hover:opacity-100'
                    }`}
                >
                  <img
                    src={`${henceforthApi.API_FILE_ROOT_ORIGINAL}${img}`}
                    alt={`Thumb ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = profile_img;
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Information */}
        <div className="flex-1 space-y-3">
          {/* Title & Price */}
          <div>
            <Link
              to={`/product/${product?._id}`}
              className="inline-flex items-center gap-1.5 text-base sm:text-lg font-bold text-slate-900 hover:text-[#1AA7DE] transition-colors group"
            >
              <span>{product?.name || language_data?.[authState.lang]?.common_not_available || 'Product Item'}</span>
              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#1AA7DE] transition-colors" />
            </Link>

            {/* Price row */}
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-xl sm:text-2xl font-black text-slate-900">
                ${numberWithCommas(product?.discount_price || product?.price || 0)}
              </span>
              {product?.price && product?.discount_price && product.price !== product.discount_price && (
                <span className="text-xs sm:text-sm font-semibold text-slate-400 line-through">
                  ${numberWithCommas(product.price)}
                </span>
              )}
              <span className="text-xs font-semibold text-slate-500">Excluding Tax</span>
            </div>
          </div>

          {/* Description */}
          {description && (
            <div className="space-y-0.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                {language_data?.[authState.lang]?.common_product || 'Product'}{' '}
                {language_data?.[authState.lang]?.common_description || 'Description'}
              </span>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal m-0">
                {isLongDescription && !showMore ? `${description.slice(0, 300)}...` : description}
              </p>
              {isLongDescription && (
                <button
                  type="button"
                  onClick={() => setShowMore(!showMore)}
                  className="text-xs font-bold text-[#1AA7DE] hover:underline cursor-pointer"
                >
                  {showMore
                    ? language_data?.[authState.lang]?.common_read_less || 'Read Less'
                    : language_data?.[authState.lang]?.common_read_more || 'Read More'}
                </button>
              )}
            </div>
          )}

          {/* Key Parameters Tiles - Borderless Clean Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 pt-2">
            <div>
              <span className="text-[11px] font-medium text-slate-400 block">{language_data?.[authState.lang]?.common_order || 'Order'} ID</span>
              <span className="text-xs sm:text-sm font-bold text-slate-800">{orderD?.order_id || 'N/A'}</span>
            </div>

            <div>
              <span className="text-[11px] font-medium text-slate-400 block">{language_data?.[authState.lang]?.common_product || 'Product'} ID</span>
              <span className="text-xs sm:text-sm font-bold text-slate-800">{product?.product_id || 'N/A'}</span>
            </div>

            <div>
              <span className="text-[11px] font-medium text-slate-400 block">{language_data?.[authState.lang]?.common_brand || 'Brand'}</span>
              <span className="text-xs sm:text-sm font-bold text-slate-800">{product?.brand?.name || 'N/A'}</span>
            </div>

            <div>
              <span className="text-[11px] font-medium text-slate-400 block">Original Price</span>
              <span className="text-xs sm:text-sm font-bold text-slate-800">${numberWithCommas(product?.price || 0)}</span>
            </div>

            <div>
              <span className="text-[11px] font-medium text-slate-400 block">{language_data?.[authState.lang]?.common_discount || 'Discount'}</span>
              <span className="text-xs sm:text-sm font-bold text-emerald-600">{product?.discount_percantage || 0}%</span>
            </div>

            <div>
              <span className="text-[11px] font-medium text-slate-400 block">Discounted Price</span>
              <span className="text-xs sm:text-sm font-bold text-slate-800">${numberWithCommas(product?.discount_price || 0)}</span>
            </div>
          </div>

          {/* Included Services */}
          {product?.services && Array.isArray(product.services) && product.services.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-1 text-xs">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                {language_data?.[authState.lang]?.common_services || 'Services'}:
              </span>
              {product.services.map((service: any, sIdx: number) => (
                <span
                  key={sIdx}
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  {service?.content || 'Service'}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ViewOrderDetail = () => {
  const { language_data, loading, setLoading, authState, authDispatch } = useContext(GlobalContext);

  const match = useMatch('/order/:id');
  const location = useLocation();
  const navigate = useNavigate();
  const newParam = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: newParam.has('transaction')
        ? `${language_data?.[authState.lang]?.common_all || 'All'} ${language_data?.[authState.lang]?.common_transaction || 'Transactions'
        }`
        : `${language_data?.[authState.lang]?.common_order || 'Orders'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: newParam.has('transaction') ? '/earnings/1' : `/orders/1`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_order || 'Order'} #${match?.params.id ? match.params.id.slice(-6).toUpperCase() : 'Detail'
        }`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const [orderD, setOrderD] = useState<any>({});
  const [showVerifyOtpModal, setShowVerifyOtpModal] = useState(false);
  const [tracking, setTracking] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = (text: string, idKey: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(idKey);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Copied to clipboard');
  };

  const calculateTotalProductPrice = () => {
    if (orderD?.products && Array.isArray(orderD.products)) {
      return orderD.products.reduce((sum: number, product: any) => sum + (product.price || 0), 0);
    }
    return 0;
  };

  const orderDetail = async () => {
    if (!match?.params.id) return;
    setLoading(true);
    try {
      const res = (await henceforthApi.Order.getOrderDetails(match.params.id)).data;
      if (res?.products && res?.products?.length > 0) {
        res.product_id = res.products[0];
      }
      setOrderD(res);
    } catch (err: any) {
      console.error(err?.response?.body?.error_description || err?.message || err);
      handleError(err, '', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    orderDetail();
  }, [match?.params.id]);

  const changeStatus = async (order_status: string) => {
    const items = {
      _id: match?.params.id,
      order_status,
      language: 'ENGLISH',
    } as any;

    if (
      orderD?.order_status === henceofrthEnums.OrderStatus.PLACED &&
      order_status === henceofrthEnums.OrderStatus.SHIPPED &&
      orderD?.delivery_type !== 'PICKUP'
    ) {
      if (tracking.trim()) {
        items.tracking_link = tracking.trim();
      } else {
        return toast.warn(
          `${language_data?.[authState.lang]?.common_please_enter_tracking_id_or_link || 'Please enter tracking ID or link'}`
        );
      }
    }

    Swal.fire({
      title: `${language_data?.[authState.lang]?.common_are_you_sure || 'Are you sure'}?`,
      text: `${language_data?.[authState.lang]?.common_you_want || 'Do you want to change order status to'} ${order_status.replace(/_/g, ' ')}?`,
      icon: 'warning',
      confirmButtonColor: '#1AA7DE',
      showDenyButton: true,
      denyButtonText: `${language_data?.[authState.lang]?.common_no || 'No'}`,
      confirmButtonText: `${language_data?.[authState.lang]?.common_yes || 'Yes, update'}`,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl font-bold px-4 py-2 text-sm',
        cancelButton: 'rounded-xl font-bold px-4 py-2 text-sm',
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Delivery.changesStatus(items);
          await orderDetail();

          Swal.fire({
            icon: 'success',
            title: `${language_data?.[authState.lang]?.common_delivery || 'Status Updated'}`,
            text: `${language_data?.[authState.lang]?.common_your_delivery_step_has_been_changed_into || 'Order status changed to'} ${order_status.replace(/_/g, ' ')}.`,
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
            customClass: { popup: 'rounded-2xl' },
          } as any);
        } catch (err: any) {
          console.error(err?.response?.body?.error_description || err?.message || err);
          handleError(err, 'active', authDispatch);
        }
      }
    });
  };

  const cancelRequest = (id: string) => {
    Swal.fire({
      title: `${language_data?.[authState.lang]?.common_are_you_sure || 'Are you sure'}?`,
      text: `${language_data?.[authState.lang]?.common_are_sure_want_to || 'Are you sure you want to cancel this request'}!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1AA7DE',
      cancelButtonColor: '#ef4444',
      cancelButtonText: `${language_data?.[authState.lang]?.common_cancel || 'Cancel'}`,
      confirmButtonText: `${language_data?.[authState.lang]?.common_yes || 'Yes'}, ${language_data?.[authState.lang]?.common_accept || 'Accept'}`,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl font-bold px-4 py-2 text-sm',
        cancelButton: 'rounded-xl font-bold px-4 py-2 text-sm',
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Order.editCancelOrder({
            _id: id,
            language: 'ENGLISH',
          });
          orderDetail();
          Swal.fire({
            icon: 'success',
            title: `${language_data?.[authState.lang]?.common_cancellation || 'Cancellation'} ${language_data?.[authState.lang]?.common_request || 'Request'}`,
            text: `${language_data?.[authState.lang]?.common_you_have_accepted || 'You have accepted the cancellation request'}.`,
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
            customClass: { popup: 'rounded-2xl' },
          });
        } catch (err: any) {
          handleError(err, 'active', authDispatch);
          console.error(err?.response?.body?.error_description || err?.message || err);
        }
      }
    });
  };

  const handleSendOtp = async () => {
    try {
      const items = {
        _id: match?.params.id,
        language: 'ENGLISH',
      };
      await henceforthApi.Order.sendPickupOtp(items);

      Swal.fire({
        icon: 'success',
        title: `${language_data?.[authState.lang]?.common_otp || 'OTP'} ${language_data?.[authState.lang]?.common_success || 'Sent'}`,
        text: `${language_data?.[authState.lang]?.common_otp_sent_successfully || 'OTP sent successfully to customer'}.`,
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl' },
      });
    } catch (err: any) {
      console.error(err?.response?.body?.error_description || err?.message || err);
      handleError(err, 'active', authDispatch);
    }
  };

  const handleVerifyOtpClick = () => {
    setShowVerifyOtpModal(true);
  };

  const handleVerifyOtpSuccess = async () => {
    await orderDetail();
  };

  const handleInvoice = async () => {
    if (orderD?.invoice_urls?.seller_invoice_url) {
      window.open(orderD.invoice_urls.seller_invoice_url, '_blank');
    } else {
      toast.info('Invoice document is not available for this order.');
    }
  };

  const statusBadge = getStatusBadge(orderD?.order_status);
  const isPickup = orderD?.delivery_type === 'PICKUP';
  const isDelivered = orderD?.order_status === henceofrthEnums.OrderStatus.DELIVERED;
  const isCancelled = orderD?.order_status === henceofrthEnums.OrderStatus.CANCELLED;

  return (
    <Fragment>
      {/* Standard Breadcrumb */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      {loading ? (
        <div className="w-full min-h-[60vh] flex flex-col items-center justify-center py-20 gap-3">
          <Spinner color="text-[#1AA7DE]" />
          <p className="text-sm font-semibold text-slate-500">Loading order details...</p>
        </div>
      ) : (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
          {/* 1. Header Card with Title, Order Status Badge & Action Buttons */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                  {language_data?.[authState.lang]?.common_order || 'Order'} #{orderD?.order_id || match?.params.id?.slice(-8).toUpperCase()}
                </h1>

                {/* Status Badge */}
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs ${statusBadge.pill}`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusBadge.dot}`} />
                  {statusBadge.label}
                </span>

                {/* Copy Button */}
                <button
                  type="button"
                  onClick={() => copyToClipboard(orderD?.order_id || match?.params.id || '', 'top_order_id')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                  title="Copy Order ID"
                >
                  {copiedId === 'top_order_id' ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-600">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
                Placed on {orderD?.created_at ? moment(orderD.created_at).format('DD MMM YYYY, hh:mm A') : 'N/A'} • Delivery: {isPickup ? 'Store Pickup' : 'Standard Shipping'} • Payment: {orderD?.payment_status || 'N/A'}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handleInvoice}
                className="gap-1.5 font-bold text-xs sm:text-sm"
              >
                <FileText className="w-4 h-4 text-slate-500" />
                <span>
                  {language_data?.[authState.lang]?.common_download_seller_invoice || 'Download Invoice'}
                </span>
              </Button>

              {!isDelivered && (
                <>
                  {isCancelled ? (
                    <Button variant="outline" size="sm" disabled className="bg-rose-50 text-rose-700 border-rose-200 font-bold text-xs sm:text-sm">
                      {language_data?.[authState.lang]?.common_order || 'Order'} {language_data?.[authState.lang]?.common_canceled || 'Cancelled'}
                    </Button>
                  ) : orderD?.cancel_requested ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => cancelRequest(orderD?._id)}
                      className="gap-1.5 font-bold text-xs sm:text-sm"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span>{language_data?.[authState.lang]?.common_accept_cancellation_request || 'Accept Cancellation'}</span>
                    </Button>
                  ) : (
                    <Button variant="destructive" size="sm" asChild className="gap-1.5 font-bold text-xs sm:text-sm">
                      <Link to={`/order/cancel/${match?.params.id}/${orderD?.order_object_id}/${orderD?.product_id?._id}`}>
                        <AlertCircle className="w-4 h-4" />
                        <span>{language_data?.[authState.lang]?.common_order || 'Cancel'} {language_data?.[authState.lang]?.common_cancel || 'Order'}</span>
                      </Link>
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* 2. Products Detail Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-4">
            <div className="flex items-center justify-between gap-3.5 pb-4 border-b border-slate-100 flex-wrap">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                    {language_data?.[authState.lang]?.common_product || 'Product'} {language_data?.[authState.lang]?.common_deatil || 'Details'}
                  </h2>
                  <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                    Ordered items, pricing, specifications, and included services
                  </p>
                </div>
              </div>

              <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                {orderD?.products?.length || 0} {orderD?.products?.length === 1 ? 'Item' : 'Items'}
              </span>
            </div>

            {/* Product items list with clean divider between items */}
            <div className="space-y-6">
              {orderD?.products && Array.isArray(orderD.products) && orderD.products.length > 0 ? (
                orderD.products.map((product: any, idx: number) => (
                  <Fragment key={product._id || idx}>
                    <ProductItem
                      product={product}
                      orderD={orderD}
                      language_data={language_data}
                      authState={authState}
                    />
                    {idx < orderD.products.length - 1 && <div className="h-px bg-slate-100 my-2" />}
                  </Fragment>
                ))
              ) : (
                <div className="py-8 text-center text-slate-400 font-semibold text-sm">
                  No products found in this order.
                </div>
              )}
            </div>
          </div>

          {/* 3. Order Breakdown Card (Price Breakup & Earning Breakdown - Clean Side by Side) */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-4">
            <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                  {language_data?.[authState.lang]?.common_order || 'Order'} {language_data?.[authState.lang]?.common_breakdown || 'Breakdown'}
                </h2>
                <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                  Complete breakdown of customer payment and seller earnings
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 pt-2">
              {/* Column 1: Price Breakup */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-slate-900 m-0 pb-1">
                  Price Breakup
                </h3>
                <ul className="list-none p-0 m-0 space-y-2.5 text-xs sm:text-sm">
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Delivery Type:</span>
                    <span className="font-bold text-slate-800">{orderD?.delivery_type || 'N/A'}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Product Price (Sum):</span>
                    <span className="font-bold text-slate-800">${numberWithCommas(calculateTotalProductPrice())}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Total Discount:</span>
                    <span className="font-bold text-rose-600">-${numberWithCommas(orderD?.discount_price || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Delivery Charges:</span>
                    <span className="font-bold text-slate-800">${numberWithCommas(orderD?.delivery_price || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Tax:</span>
                    <span className="font-bold text-slate-800">${numberWithCommas(orderD?.tax_amount || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Platform Fee:</span>
                    <span className="font-bold text-slate-800">${numberWithCommas(orderD?.platform_fee || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between pt-3 border-t border-slate-100 text-slate-900">
                    <span className="text-sm font-black">Total Amount:</span>
                    <span className="text-base sm:text-lg font-black">${numberWithCommas(orderD?.total_price || 0)}</span>
                  </li>
                </ul>
              </div>

              {/* Column 2: Earning Breakdown */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-slate-900 m-0 pb-1">
                  Earning Breakdown
                </h3>
                <ul className="list-none p-0 m-0 space-y-2.5 text-xs sm:text-sm">
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Total Amount:</span>
                    <span className="font-bold text-slate-800">${numberWithCommas(orderD?.total_price || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Admin Commission:</span>
                    <span className="font-bold text-rose-600">-${numberWithCommas(orderD?.admin_commision || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Platform Fee:</span>
                    <span className="font-bold text-rose-600">-${numberWithCommas(orderD?.platform_fee || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between py-1 text-slate-900">
                    <span className="font-bold">Total Earnings:</span>
                    <span className="font-black text-emerald-600 text-base sm:text-lg">${numberWithCommas(orderD?.total_earnings || 0)}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Order Quantity:</span>
                    <span className="font-bold text-slate-800">{orderD?.quantity || 1}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-600">
                    <span className="font-medium">Payment Status:</span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${orderD?.payment_status === 'SUCCESS'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-amber-50 text-amber-700'
                        }`}
                    >
                      {orderD?.payment_status || 'N/A'}
                    </span>
                  </li>
                  <li className="flex items-center justify-between text-slate-500 text-xs pt-1">
                    <span>Created At:</span>
                    <span>{orderD?.created_at ? moment(orderD.created_at).format('DD MMM YYYY, hh:mm A') : 'N/A'}</span>
                  </li>
                  <li className="flex items-center justify-between text-slate-500 text-xs">
                    <span>Updated At:</span>
                    <span>{orderD?.updated_at ? moment(orderD.updated_at).format('DD MMM YYYY, hh:mm A') : 'N/A'}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 4. Customer Details & Order Status Cards in 2 Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
            {/* Customer Details Card (5 cols) */}
            <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-4">
              <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                    {language_data?.[authState.lang]?.common_user || 'User'} {language_data?.[authState.lang]?.common_deatil || 'Details'}
                  </h2>
                  <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                    Customer contact profile and delivery destination
                  </p>
                </div>
              </div>

              {/* Profile Image & Name */}
              <div className="flex items-center gap-3.5 pt-1">
                <img
                  src={
                    orderD?.user_id?.profile_pic
                      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${orderD.user_id.profile_pic}`
                      : profile_img
                  }
                  alt={orderD?.user_id?.name || 'Customer'}
                  className="w-12 h-12 rounded-full object-cover bg-slate-100"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = profile_img;
                  }}
                />
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 m-0">
                    {orderD?.user_id?.name || language_data?.[authState.lang]?.common_not_available || 'Guest Customer'}
                  </h3>
                  <span className="text-xs text-slate-400 font-medium">Customer</span>
                </div>
              </div>

              {/* Contact Information (Clean List, No Inner Box Borders) */}
              <div className="space-y-3 pt-1 text-xs sm:text-sm">
                {orderD?.user_id?.email && (
                  <div className="flex items-center justify-between text-slate-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-medium truncate">{orderD.user_id.email}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(orderD.user_id.email, 'cust_email')}
                      className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                      title="Copy Email"
                    >
                      {copiedId === 'cust_email' ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                )}

                {orderD?.user_id?.phone_no &&
                  String(orderD.user_id.phone_no).trim() !== '0' && (
                    <div className="flex items-center justify-between text-slate-700">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Phone className="w-4 h-4 text-slate-400 shrink-0" />

                        <span className="font-medium truncate">
                          {orderD.user_id.country_code
                            ? `+${orderD.user_id.country_code} `
                            : ''}
                          {orderD.user_id.phone_no}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(
                            `+${orderD.user_id.country_code || ''}${orderD.user_id.phone_no}`,
                            'cust_phone'
                          )
                        }
                        className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                        title="Copy Phone"
                      >
                        {copiedId === 'cust_phone' ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  )}
                <div className="flex items-start gap-2.5 text-slate-700 pt-0.5">
                  <MapPin className="w-4 h-4 text-[#1AA7DE] shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                      {isPickup ? 'Store Location' : 'Delivery Address'}
                    </span>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium m-0 mt-0.5">
                      {orderD?.address_id?.full_address ||
                        orderD?.address_id?.address ||
                        language_data?.[authState.lang]?.common_not_available ||
                        'No address provided'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Status & Actions Card (7 cols) */}
            <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-4">
              <div className="flex items-center justify-between gap-3.5 pb-4 border-b border-slate-100 flex-wrap">
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                      {language_data?.[authState.lang]?.common_order || 'Order'} {language_data?.[authState.lang]?.common_status || 'Status'}
                    </h2>
                    <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                      Fulfillment tracking, dispatch controls, and OTP verification
                    </p>
                  </div>
                </div>

                {/* Status action buttons */}
                {!orderD?.cancel_request_accepted &&
                  !orderD?.cancel_requested &&
                  orderD?.order_status !== henceofrthEnums.OrderStatus.PENDING_CANCELLATION &&
                  orderD?.order_status !== henceofrthEnums.OrderStatus.CANCELLED &&
                  !isDelivered && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Tracking input for standard delivery */}
                      {orderD?.order_status === henceofrthEnums.OrderStatus.PLACED && !isPickup && (
                        <div className="relative min-w-[200px]">
                          <input
                            type="text"
                            value={tracking}
                            onChange={(e) => setTracking(e.target.value)}
                            placeholder={language_data?.[authState.lang]?.common_please_enter_tracking_id_or_link || 'Tracking ID / Link'}
                            className="w-full px-3 py-1.5 text-xs rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]"
                          />
                        </div>
                      )}

                      {/* Pickup OTP buttons */}
                      {isPickup && orderD?.order_status === henceofrthEnums.OrderStatus.PLACED && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSendOtp}
                            className="font-bold text-xs"
                          >
                            Resend OTP
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleVerifyOtpClick}
                            className="gap-1.5 font-bold text-xs"
                          >
                            <ShieldCheck className="w-4 h-4" />
                            Verify OTP
                          </Button>
                        </>
                      )}

                      {/* Proceed to Shipped */}
                      {!isPickup && orderD?.order_status === henceofrthEnums.OrderStatus.PLACED && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => changeStatus(henceofrthEnums.OrderStatus.SHIPPED)}
                          className="gap-1.5 font-bold text-xs"
                        >
                          <Truck className="w-4 h-4" />
                          Proceed to Shipped
                        </Button>
                      )}

                      {/* Proceed to Delivered */}
                      {orderD?.order_status === henceofrthEnums.OrderStatus.SHIPPED && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => changeStatus(henceofrthEnums.OrderStatus.DELIVERED)}
                          className="gap-1.5 font-bold text-xs"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Proceed to Delivered
                        </Button>
                      )}
                    </div>
                  )}
              </div>

              {/* Status details body */}
              <div className="space-y-3 pt-1">
                {/* Cancellation Request Section */}
                {orderD?.cancel_requested && (
                  <div className="p-4 rounded-xl bg-rose-50/80 space-y-2">
                    <div className="flex items-center gap-2 text-rose-800 font-bold text-xs uppercase tracking-wider">
                      <AlertTriangle className="w-4 h-4 text-rose-600" />
                      <span>{language_data?.[authState.lang]?.common_cancel || 'Cancel'} {language_data?.[authState.lang]?.common_reason || 'Reason'}</span>
                    </div>
                    <p className="text-xs text-rose-700 font-medium m-0 pl-6">
                      {orderD?.cancel_reason || 'Customer requested order cancellation.'}
                    </p>

                    {orderD?.cancel_description && (
                      <div className="pl-6 pt-1">
                        <span className="text-[11px] font-bold text-rose-800 block">
                          {language_data?.[authState.lang]?.common_cancel || 'Cancel'} {language_data?.[authState.lang]?.common_description || 'Details'}:
                        </span>
                        <p className="text-xs text-rose-700 font-medium m-0 mt-0.5">
                          {orderD.cancel_description}
                        </p>
                      </div>
                    )}

                    <div className="pt-2 pl-6">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => cancelRequest(orderD?._id)}
                        className="font-bold text-xs"
                      >
                        {language_data?.[authState.lang]?.common_accept || 'Accept Cancellation Request'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Delivery Status Summary - Clean Lines, No Box Border */}
                <div className="space-y-3 text-xs sm:text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-600">
                      {language_data?.[authState.lang]?.common_delivery || 'Delivery'} {language_data?.[authState.lang]?.common_status || 'Status'}:
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusBadge.pill}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${statusBadge.dot}`} />
                      {statusBadge.label}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-slate-500 pt-1 border-t border-slate-100">
                    <span>Fulfillment Method:</span>
                    <span className="font-semibold text-slate-800">
                      {isPickup ? 'Store Self-Pickup' : 'Standard Courier Delivery'}
                    </span>
                  </div>

                  {orderD?.tracking_link && (
                    <div className="flex items-center justify-between text-slate-500 pt-1 border-t border-slate-100">
                      <span>Tracking Info:</span>
                      <span className="font-semibold text-[#1AA7DE] truncate max-w-[250px]">
                        {orderD.tracking_link}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 5. Ratings & Reviews Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-4">
            <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                  {language_data?.[authState.lang]?.common_rating_reviews || 'Ratings & Reviews'}
                </h2>
                <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                  Verified buyer feedback and satisfaction ratings
                </p>
              </div>
            </div>

            {(() => {
              const reviews = orderD?.product_id?.reviews || orderD?.product_id?.ratings || [];
              if (!Array.isArray(reviews) || reviews.length === 0) {
                return (
                  <p className="text-xs sm:text-sm text-slate-500 font-medium py-2 m-0">
                    {language_data?.[authState.lang]?.common_not_available || 'No reviews available yet for this product.'}
                  </p>
                );
              }

              return (
                <div className="space-y-4">
                  {reviews.map((rev: any, rIdx: number) => {
                    const stars = rev?.ratings || 5;
                    const userPic = rev?.user?.profile_pic || rev?.user_id?.profile_pic || rev?.profile_pic;
                    const userName = rev?.user?.name || rev?.user_id?.name || rev?.user_name || 'Customer';

                    return (
                      <div
                        key={rIdx}
                        className="pb-4 border-b border-slate-100 last:border-0 last:pb-0 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={
                                userPic
                                  ? `${henceforthApi.API_FILE_ROOT_MEDIUM}${userPic}`
                                  : profile_img
                              }
                              alt={userName}
                              className="w-8 h-8 rounded-full object-cover bg-slate-100"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = profile_img;
                              }}
                            />
                            <div>
                              <h4 className="text-xs sm:text-sm font-bold text-slate-900 m-0">{userName}</h4>
                              <div className="flex items-center gap-0.5 mt-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-3.5 h-3.5 ${i < stars ? 'text-amber-400 fill-amber-400' : 'text-slate-200'
                                      }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          {rev?.created_at && (
                            <span className="text-[11px] font-medium text-slate-400">
                              {moment(Number(rev.created_at)).format('DD MMM YYYY')}
                            </span>
                          )}
                        </div>

                        {rev?.title && <p className="text-xs font-bold text-slate-800 m-0 pt-1">{rev.title}</p>}
                        {rev?.description && (
                          <p className="text-xs text-slate-600 font-medium leading-relaxed m-0">
                            {rev.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Verify Pickup OTP Modal */}
      <VerifyPickupOtpModal
        show={showVerifyOtpModal}
        handleClose={() => setShowVerifyOtpModal(false)}
        orderId={match?.params.id || ''}
        onVerifySuccess={handleVerifyOtpSuccess}
      />
    </Fragment>
  );
};

export default ViewOrderDetail;